import type { Transaction } from '@hero/schema';
import { formatDateMain } from '@hero/core';
import { useTranslation } from 'react-i18next';
import { TransactionStatusBadge } from '../Status/TransactionStatusBadge';
import { DrawerContentList } from '../../../app/Drawer/DrawerContentList';
import { PriceBadge } from '../../../app/Price/PriceBadge';
import { Price } from '../../../app/Price';

export interface TransactionSummaryTabProps {
  transaction: Transaction;
}

export function TransactionSummaryTab({ transaction }: TransactionSummaryTabProps) {
  const { t } = useTranslation(['m-payment']);

  const typeLabel = t(`m-payment:transaction.type.${transaction.type}`);
  const flowLabel =
    transaction.flow === 1
      ? t('m-payment:transaction.flow.credit')
      : t('m-payment:transaction.flow.debit');

  const dateDisplay = transaction.createdAt
    ? formatDateMain({ format: 'date_time', date: transaction.createdAt })
    : undefined;

  const isFailed = transaction.status === 'failed';
  const isCredit = transaction.flow === 1;
  const priceColor = isFailed ? 'gray' : isCredit ? 'green' : 'red';

  const gatewayLabel = transaction.gateway?.method
    ? t(`m-payment:payment_methods.${transaction.gateway.method}`, {
        defaultValue: transaction.gateway.method,
      })
    : undefined;

  return (
    <div className="flex flex-col px-5 py-2">
      <DrawerContentList
        list={[
          {
            label: t('m-payment:transaction.fields.reference'),
            value: transaction.ref.main || transaction.ref.short,
          },
          {
            label: t('m-payment:transaction.fields.status'),
            value: <TransactionStatusBadge status={transaction.status} />,
          },
          { label: t('m-payment:transaction.fields.type'), value: typeLabel },
          { label: t('m-payment:transaction.fields.flow'), value: flowLabel },
          {
            label: t('m-payment:transaction.fields.created_at'),
            value: dateDisplay ?? '-',
          },
          {
            label: t('m-payment:transaction.fields.notes'),
            value: transaction.notes || '-',
            hidden: !transaction.notes,
          },
          {
            label: t('m-payment:transaction.fields.amount'),
            value: (
              <PriceBadge
                price={transaction.amount}
                currency={transaction.currency}
                color={priceColor}
              />
            ),
          },
          {
            label: t('m-payment:transaction.fields.credit_amount'),
            value: <Price amount={transaction.creditAmount} currency={transaction.currency} />,
          },
          {
            label: t('m-payment:transaction.fields.debit_amount'),
            value: <Price amount={transaction.debitAmount} currency={transaction.currency} />,
          },
          {
            label: t('m-payment:transaction.fields.balance'),
            value:
              transaction.wallet?.balance != null
                ? <Price amount={transaction.wallet.balance} currency={transaction.currency} />
                : '-',
          },
          {
            label: t('m-payment:transaction.fields.gateway'),
            value: gatewayLabel ?? '-',
            hidden: !gatewayLabel,
          },
        ]}
      />
    </div>
  );
}
